import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Cpu, CheckCircle, XCircle } from 'lucide-react';

interface SystemSignature {
  id: string;
  signature: string;
  platform: string;
  cpuId?: string;
  macAddress?: string;
  diskSerial?: string;
  status: string;
  createdAt: number;
  verifiedAt?: number;
}

function SystemSignatures() {
  const [signatures, setSignatures] = useState<SystemSignature[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [selectedSignatureId, setSelectedSignatureId] = useState<string | null>(null);

  useEffect(() => {
    fetchSignatures();
  }, []);

  const fetchSignatures = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/system-signatures`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSignatures(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch system signatures:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/system-signatures/${id}/approve`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchSignatures();
      }
    } catch (error) {
      console.error('Failed to approve signature:', error);
    }
  };

  const handleRejectSignature = async () => {
    if (!selectedSignatureId) return;

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/system-signatures/${selectedSignatureId}/reject`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setRejectDialogOpen(false);
        setSelectedSignatureId(null);
        fetchSignatures();
      }
    } catch (error) {
      console.error('Failed to reject signature:', error);
    }
  };

  const handleRevokeSignature = async () => {
    if (!selectedSignatureId) return;

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/system-signatures/${selectedSignatureId}/revoke`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setRevokeDialogOpen(false);
        setSelectedSignatureId(null);
        fetchSignatures();
      }
    } catch (error) {
      console.error('Failed to revoke signature:', error);
    }
  };

  const openRejectDialog = (id: string) => {
    setSelectedSignatureId(id);
    setRejectDialogOpen(true);
  };

  const openRevokeDialog = (id: string) => {
    setSelectedSignatureId(id);
    setRevokeDialogOpen(true);
  };

  const getStatusBadgeColor = (status: string) => {
    const colors: Record<string, string> = {
      PENDING: 'bg-yellow-500',
      VERIFIED: 'bg-green-500',
      REVOKED: 'bg-red-500',
      EXPIRED: 'bg-gray-500',
    };
    return colors[status] || 'bg-gray-500';
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">Loading...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Cpu className="h-8 w-8" />
            System Signatures
          </h2>
          <p className="text-muted-foreground mt-2">
            Hardware fingerprinting and device management
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All System Signatures</CardTitle>
          <CardDescription>
            {signatures.length} system signature{signatures.length !== 1 ? 's' : ''} registered
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Signature</TableHead>
                <TableHead>Platform</TableHead>
                <TableHead>CPU ID</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {signatures.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground">
                    No system signatures found
                  </TableCell>
                </TableRow>
              ) : (
                signatures.map((sig) => (
                  <TableRow key={sig.id}>
                    <TableCell className="font-mono text-xs max-w-xs truncate">
                      {sig.signature}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{sig.platform}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs max-w-xs truncate">
                      {sig.cpuId || '-'}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusBadgeColor(sig.status)}>
                        {sig.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(sig.createdAt * 1000).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      {sig.status === 'PENDING' && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleApprove(sig.id)}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openRejectDialog(sig.id)}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                        </>
                      )}
                      {sig.status === 'VERIFIED' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openRevokeDialog(sig.id)}
                        >
                          Revoke
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Reject Confirmation Dialog */}
      <AlertDialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject System Signature</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to reject this system signature? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRejectSignature}>
              Reject
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revoke Confirmation Dialog */}
      <AlertDialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke System Signature</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to revoke this system signature? This will invalidate any licenses bound to this hardware.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRevokeSignature}>
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default SystemSignatures;
